import {
  Css_Text_16_24,
  Css_Text_20_30,
  GridWithGaps,
  Text_16_24,
} from '@dreamstack/feature-components'
import { ArrowRight } from '@dreamstack/icons'
import { SimpleTextLink } from '@dreamstack/simple-components'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import tw, { styled } from 'twin.macro'

const TwoColumnContainer = tw.div`flex flex-col w-full md:(flex-row space-x-px-48) md:(mt-px-32)`
const Column = tw.div`flex-1 flex flex-col max-w-full justify-center text-left mt-px-48 md:(mt-0)`
const ImageWrapper = tw.div`height[208px] md:(height[245px]) ml:(height[335px]) relative col-start-1 col-span-7`
const ContentWrapper = tw.div`col-span-6 z-10 bg-accentroGray-full text-accentroWhite-full `
const FloorWrapper = tw.div`flex flex-col ml-px-64 md:(m-0)`
const HeadingWrapper = tw.div`flex flex-col md:flex-row my-px-24 `
const PropertyPrice = tw.div`text-accentroWhite-full text-px-28 leading-px-42 font-semibold`
const PropertyPreviewDiv = tw.div`flex flex-col w-full pt-0 px-px-24 pb-px-24 md:(px-px-32 pb-px-40)`

const GreenRectangle = styled.div(() => [
  Css_Text_16_24,
  tw`bg-accentroLightGreen-full text-accentroGray-full w-max h-auto px-px-8 py-px-4 font-semibold mt-px-24`,
])

const PropertyHeading = styled.div(() => [
  Css_Text_20_30,
  tw`flex-1 overflow-hidden overflow-ellipsis break-words text-accentroWhite-full font-semibold`,
])
const PropertyBody = styled.div(() => [
  Css_Text_16_24,
  tw`flex-1 overflow-hidden overflow-ellipsis text-accentroWhite-full`,
])

type BadgeColors = 'default' | 'green'

const StyledBadge = styled.div<{
  smalltext: boolean
  color: BadgeColors
}>(({ smalltext, color }) => [
  smalltext ? tw`text-base` : tw`text-2xl`,
  color === 'default' && tw`bg-accentroAquaLight-full text-accentroGray-full`,
  color === 'green' && tw`bg-accentroLightGreen-full text-accentroWhite-full`,
  tw` rounded-full transform -rotate-12 absolute font-semibold
  width[115px] height[115px]  flex flex-col justify-center items-center -bottom-px-16 -right-px-12 text-center z-50 md:(right-px-24) `,
])

const properties = [
  {
    imgSrc: '/static/images/SideBySide1.jpeg',
    badgeContent: 'Vacant & Renovated',
    badgeColor: 'default',
    smalltext: true,
    heading: 'Torstraße 225e, Berlin-Mitte',
    sqrMeters: '64.08',
    floor: '3',
    price: '569,900.00',
    pricePerSqm: '8,893.57',
  },
  {
    imgSrc: '/static/images/SideBySide2.jpeg',
    badgeContent: 'Rented',
    badgeColor: 'green',
    smalltext: false,
    heading: 'Torstraße 225e, Berlin-Mitte',
    sqrMeters: '63.04',
    floor: '2',
    price: '264,410.00',
    pricePerSqm: '4,194.32',
    greenRectangle: '52% cheaper!',
  },
]

export const SideBySidePropertyComparison: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  return (
    <GridWithGaps>
      <div tw="col-span-full">
        <TwoColumnContainer>
          {properties.map((property, index) => (
            <Column key={index}>
              <ImageWrapper>
                <Image
                  src={property.imgSrc}
                  alt="Property"
                  layout="fill"
                  objectFit="cover"
                />
                <StyledBadge
                  smalltext={property.smalltext}
                  color={property.badgeColor as BadgeColors}
                >
                  {property.badgeContent}
                </StyledBadge>
              </ImageWrapper>
              <ContentWrapper>
                <PropertyPreviewDiv>
                  <HeadingWrapper>
                    <PropertyHeading>
                      <div tw="w-2/3 md:(w-1/2) ml:(w-full)">
                        {property.heading}
                      </div>
                    </PropertyHeading>
                  </HeadingWrapper>
                  <div tw="flex flex-row">
                    <div tw="flex flex-col">
                      <PropertyHeading tw="md:(mr-px-80)">
                        {property.sqrMeters} sqm
                      </PropertyHeading>
                      <PropertyBody>Living space</PropertyBody>
                    </div>
                    <FloorWrapper>
                      <PropertyHeading>{property.floor}</PropertyHeading>
                      <PropertyBody>Floor</PropertyBody>
                    </FloorWrapper>
                  </div>
                  {property.greenRectangle ? (
                    <GreenRectangle>{property.greenRectangle}</GreenRectangle>
                  ) : (
                    <div tw="height[52px]" />
                  )}
                  <div tw="mt-px-12">
                    <div tw="flex flex-row">
                      <PropertyPrice>{property.price}</PropertyPrice>
                      <PropertyHeading tw=" m-px-8">EUR</PropertyHeading>
                    </div>
                    <Text_16_24 tw="text-accentroWhite-500">
                      {property.pricePerSqm} EUR per m²
                    </Text_16_24>
                  </div>
                </PropertyPreviewDiv>
              </ContentWrapper>
            </Column>
          ))}
        </TwoColumnContainer>
        <div tw="flex margin-top[52px] md:margin-top[65px]">
          <SimpleTextLink
            target="_blank"
            href="https://accentro.de/en/real-estate"
            tw="font-semibold md:(text-xl) flex items-center justify-end"
          >
            See all ACCENTRO Properties
            <ArrowRight tw="w-px-32 h-px-32 ml-px-12" />
          </SimpleTextLink>
        </div>
      </div>
    </GridWithGaps>
  )
}
